import type { RagAlert } from "../types/ragChatTypes";
import { ALERT_LEVEL_ORDER } from "../constants/ragUiConstants";

export type FlattenedPageAlert = RagAlert & { page: string; key: string };

/** Matches "**Warnings and Notes**" (or variants) as a section heading. */
const WARNINGS_SECTION_RE =
  /(?:^|\n)\s*\*{0,2}\s*warnings?\s*(?:and|\/|&)\s*notes?\s*:?\s*\*{0,2}\s*(?:\n|$)/i;

const POST_INSTALL_SECTION_RE =
  /(?:^|\n)\s*\*{0,2}\s*post[-\s]?install\s*\*{0,2}\s*:?\s*(?:\n|$)/i;

function normalizeAlertText(raw: string): string {
  return raw
    .replace(/^\*{1,2}|\*{1,2}$/g, "")
    .replace(/^[⚠️⛔⚡ℹ️📘📝•\-*]\s*/u, "")
    .trim();
}

function inferAlertLevel(line: string): string {
  const lower = line.toLowerCase();
  if (/^⛔|\bdanger\b/.test(line)) return "danger";
  if (/^⚠|warning|\bdo not\b|\bmust not\b/.test(lower)) return "warning";
  if (/^⚡|\bcaution\b/.test(lower)) return "caution";
  return "note";
}

/** Parse bullet lines from the LLM "Warnings and Notes" block. */
export function parseWarningsBlock(block: string): RagAlert[] {
  const alerts: RagAlert[] = [];
  const seen = new Set<string>();

  for (const line of block.split("\n")) {
    let trimmed = line.trim();
    if (!trimmed) continue;
    if (/^\*{0,2}\s*warnings?\s*(?:and|\/|&)\s*notes?\s*:?\s*\*{0,2}\s*$/i.test(trimmed)) {
      continue;
    }

    if (/^[-*•]\s+/.test(trimmed)) {
      trimmed = trimmed.replace(/^[-*•]\s+/, "");
    } else if (/^\d+[\.\)]\s+/.test(trimmed)) {
      continue;
    }

    const text = normalizeAlertText(trimmed);
    if (!text || text.length < 4) continue;

    const level = inferAlertLevel(trimmed);
    const key = `${level}:${text}`;
    if (seen.has(key)) continue;
    seen.add(key);
    alerts.push({ level, text });
  }
  return alerts;
}

export interface SplitAnswerResult {
  /** Answer body without trailing warnings (and without post-install block moved to end). */
  body: string;
  /** Alerts parsed from the dedicated warnings section in the answer. */
  extractedAlerts: RagAlert[];
  /** Trailing markdown after a horizontal rule (e.g. alternative-interpretation footer). */
  trailingCallout: string;
}

/**
 * Split LLM answer into main procedural text vs trailing "Warnings and Notes".
 * Backend prompts require warnings at the end of the answer string; the UI shows them first.
 *
 * Trailing `---` sections (linked procedure handoff, alternative interpretations) are
 * extracted from the full answer *before* the warnings split, because handoff answers
 * place warnings immediately before the horizontal rule.
 */
export function splitAnswerContent(answer: string): SplitAnswerResult {
  let text = (answer ?? "").trim();
  if (!text) {
    return { body: "", extractedAlerts: [], trailingCallout: "" };
  }

  let trailingCallout = "";
  const trailingHr = text.match(/\n---\n[\s\S]+$/);
  if (trailingHr?.index !== undefined) {
    trailingCallout = trailingHr[0].replace(/^\n---\n/, "").trim();
    text = text.slice(0, trailingHr.index).trim();
  }

  const warnMatch = text.match(WARNINGS_SECTION_RE);
  let beforeWarnings = text;
  let warningsTail = "";

  if (warnMatch && warnMatch.index !== undefined) {
    beforeWarnings = text.slice(0, warnMatch.index).trim();
    warningsTail = text.slice(warnMatch.index + warnMatch[0].length).trim();
  }

  const postMatch = beforeWarnings.match(POST_INSTALL_SECTION_RE);
  let mainBody = beforeWarnings;
  let postInstallBlock = "";

  if (postMatch && postMatch.index !== undefined) {
    mainBody = beforeWarnings.slice(0, postMatch.index).trim();
    postInstallBlock = beforeWarnings.slice(postMatch.index).trim();
  }

  const extractedAlerts = warningsTail ? parseWarningsBlock(warningsTail) : [];

  const body = [mainBody, postInstallBlock].filter(Boolean).join("\n\n").trim();

  return { body, extractedAlerts, trailingCallout };
}

/** True when the trailing callout is a cross-document handoff procedure block. */
export function isLinkedProcedureCallout(text: string): boolean {
  return /^#{1,3}\s*Linked Procedure\s*—/im.test(String(text ?? "").trim());
}

const SYNTHETIC_ALERT_PAGE = "0";

/** Merge API page_alerts with alerts extracted from answer markdown. */
export function mergePageAlerts(
  pageAlerts: Record<string, RagAlert[]>,
  extracted: RagAlert[]
): Record<string, RagAlert[]> {
  if (!extracted.length) return pageAlerts;

  const normalizeKey = (text: string) =>
    String(text ?? "")
      .replace(/^[-*•]\s+/, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

  const merged: Record<string, RagAlert[]> = { ...pageAlerts };
  const seen = new Set(
    Object.values(merged)
      .flat()
      .map((a) => normalizeKey(a.text))
      .filter(Boolean)
  );

  const toAdd: RagAlert[] = [];
  for (const a of extracted) {
    const key = normalizeKey(a.text);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    toAdd.push(a);
  }

  if (toAdd.length) {
    merged[SYNTHETIC_ALERT_PAGE] = [...(merged[SYNTHETIC_ALERT_PAGE] ?? []), ...toAdd];
  }

  return merged;
}

/** Flatten page_alerts into a single list sorted by page then severity. */
export function flattenSortedPageAlerts(
  pageAlerts: Record<string, RagAlert[]>
): FlattenedPageAlert[] {
  const pages = Object.keys(pageAlerts).sort(
    (a, b) => (parseInt(a, 10) || 0) - (parseInt(b, 10) || 0)
  );
  const all = pages.flatMap((page) =>
    [...(pageAlerts[page] || [])]
      .sort((a, b) => (ALERT_LEVEL_ORDER[a.level] ?? 9) - (ALERT_LEVEL_ORDER[b.level] ?? 9))
      .map((a, i) => ({ ...a, page, key: `${page}-${i}` }))
  );
  return all.sort(
    (a, b) => (ALERT_LEVEL_ORDER[a.level] ?? 9) - (ALERT_LEVEL_ORDER[b.level] ?? 9)
  );
}

export function formatAlertPage(page?: string): string | undefined {
  if (!page || page === "0" || page === "?") return undefined;
  return page;
}

export function formatAlertSourceLine(
  page?: string,
  sourceDocumentName?: string,
  pageLabelShort = "p."
): string {
  const pageNum = formatAlertPage(page);
  const parts: string[] = [];
  if (sourceDocumentName?.trim()) parts.push(sourceDocumentName.trim());
  if (pageNum) parts.push(`${pageLabelShort}${pageNum}`);
  return parts.length ? parts.join(" · ") : "From source";
}

/** Remove trailing "Source: …" metadata sometimes bundled into alert text. */
export function stripEmbeddedAlertSource(text: string): string {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return "";

  const withoutSource = trimmed
    .replace(/\.Source:\s*.+$/i, ".")
    .replace(/\s+Source:\s*.+$/i, "")
    .replace(/Source:\s*.+$/i, "")
    .trim();

  return withoutSource || trimmed;
}
